import { createClient, type Client } from "@connectrpc/connect";
import { createGrpcWebTransport } from "@connectrpc/connect-web";

import { Broadcast } from "../gen/broadcast_live_pb.js";
import { Catalog } from "../gen/catalog_pb.js";
import { API_HOST, CATALOG_HOST } from "../config.js";
import { CATALOG_RETRY, retryInterceptor } from "./retry.js";

/**
 * Is this module running inside Cloudflare's Workers runtime — the `/round/:id`
 * Pages Function (`functions/round/[id].ts`), which reads the calendar through
 * this same client? Workers set `navigator.userAgent` to exactly this string.
 */
export function onWorkersRuntime(nav: { userAgent?: string } | undefined = globalThis.navigator): boolean {
  return nav?.userAgent === "Cloudflare-Workers";
}

/**
 * connect-web asks fetch for `redirect: "error"`, which workerd refuses outright
 * ("Invalid redirect value, must be one of \"follow\" or \"manual\"") — measured
 * under `wrangler pages dev`: every catalog read from the Function failed. On
 * the edge the request asks for `manual` instead, so a redirect comes back as a
 * 3xx the transport rejects, the same outcome `error` gives a browser. Browsers
 * keep connect-web's own fetch untouched.
 */
export const workersFetch: typeof fetch = (input, init) =>
  globalThis.fetch(input, init?.redirect === "error" ? { ...init, redirect: "manual" } : init);

// grpc-web, not Connect: the two Rust services serve tonic-web in-process, which
// speaks grpc-web only. `binary` because tonic-web's JSON codec is not enabled.
const broadcastTransport = createGrpcWebTransport({ baseUrl: API_HOST });

// The catalog is read-only and every read is declared NO_SIDE_EFFECTS in the
// proto, so its transport retries a transient failure (a content-service
// restart is ~31 s of 502s from the tunnel) inside a bounded, jittered budget
// — `net/retry.ts`. The broadcast transport carries the live stream and is
// left alone: a re-subscribed stream is the round page's own reconnect.
const catalogTransport = createGrpcWebTransport({
  baseUrl: CATALOG_HOST,
  interceptors: [retryInterceptor(CATALOG_RETRY)],
  ...(onWorkersRuntime() ? { fetch: workersFetch } : {}),
});

export const broadcastClient: Client<typeof Broadcast> = createClient(Broadcast, broadcastTransport);
export const catalogClient: Client<typeof Catalog> = createClient(Catalog, catalogTransport);
